import apiClient from './api'; // Import our configured axios instance

/**
 * Signs up a new user and tenant.
 * Corresponds to: POST /api/user/signup
 * @param {string} email
 * @param {string} username
 * @param {string} password
 * @param {string} [tenantName] - Optional name for the new company/tenant.
 * @returns {Promise<object>} The user object from the server.
 */
export async function signup(email, username, password, tenantName) {
  const response = await apiClient.post('/user/signup', {
    email,
    username, // The backend now uses 'username' consistently
    password,
    tenantName,
  });
  return response.data; // response.data will contain { message, user }
}

/**
 * Logs a user in and establishes a session.
 * Corresponds to: POST /api/user/login
 * @param {string} username - Can be username or email.
 * @param {string} password
 * @returns {Promise<object>} The user object from the server.
 */
export async function login(username, password) {
  const response = await apiClient.post('/user/login', {
    username, // The backend now uses 'username' consistently
    password,
  });
  return response.data;
}

/**
 * Logs the current user out by destroying the server-side session.
 * Corresponds to: POST /api/user/logout
 */
export async function logout() {
  const response = await apiClient.post('/user/logout');
  return response.data;
}

/**
 * Checks the current session status with the server.
 * Ideal for running when the app first loads.
 * Corresponds to: GET /api/user/status
 * @returns {Promise<object>} An object containing the session user: { user: { id, username, ... } }
 */
export async function getStatus() {
  const response = await apiClient.get('/user/status');
  return response.data;
}

/**
 * Fetches the profile of the currently logged-in user.
 * Corresponds to: GET /api/user/
 */
export async function getProfile() {
  // No body is needed; the server knows who we are from the session cookie.
  const response = await apiClient.get('/user/');
  return response.data;
}

/**
 * Updates the profile of the currently logged-in user.
 * Corresponds to: PUT /api/user/
 * @param {string} currentPassword - The user's current password for verification.
 * @param {string} [newEmail]
 * @param {string} [newUsername]
 * @param {string} [newPassword]
 * @returns {Promise<object>} The updated user object.
 */
export async function updateProfile({ currentPassword, newEmail, newUsername, newPassword }) {
  // No userId in the body; the server uses the session.
  const response = await apiClient.put('/user/', {
    currentPassword,
    newEmail,
    newUsername,
    newPassword,
  });
  return response.data;
}

/**
 * Deletes the account of the currently logged-in user.
 * Corresponds to: DELETE /api/user/
 * @param {string} password - The user's password for verification.
 */
export async function deleteAccount(password) {
  // No userId in the body; the server uses the session.
  // axios.delete sends data in a `data` property in the config object.
  const response = await apiClient.delete('/user/', {
    data: { password },
  });
  return response.data;
}
